import { useState, useEffect, useRef } from 'react';

/**
 * Hook to track if a component's width is above a breakpoint threshold.
 * Uses ResizeObserver to efficiently monitor element width changes.
 * 
 * @param {number} breakpoint - Width threshold in pixels (default: 620)
 * @returns {Object} { ref, isWideEnough } - Ref to attach to element and boolean state
 * 
 * @example
 * const { ref: containerRef, isWideEnough } = useResponsiveWidth(700);
 * return <div ref={containerRef}>{isWideEnough && <Sidebar />}</div>;
 */
export const useResponsiveWidth = (breakpoint = 620) => {
  const [isWideEnough, setIsWideEnough] = useState(true);
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return undefined;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        setIsWideEnough(width >= breakpoint);
      }
    });

    resizeObserver.observe(element);

    // Set initial width
    const initialWidth = element.getBoundingClientRect().width;
    setIsWideEnough(initialWidth >= breakpoint);

    return () => resizeObserver.disconnect();
  }, [breakpoint]);

  return { ref: elementRef, isWideEnough };
};
